/*

Class : Checkout
Description : Saisie CB embarquée (Stripe.js vanilla, sans branding visible) pour
le paiement d'un panier de produits. React 16.6 (pas de hooks) → on monte les
Elements Stripe manuellement via des refs plutôt que via @stripe/react-stripe-js.
Author : Franck David

*/




import React, { Component } from 'react'
import { motion } from 'framer-motion'
import { loadStripe } from '@stripe/stripe-js'
import Loader from '../UI/Loader'

class Checkout extends Component {
	constructor(props) {
		super(props)
		this.state = {
			status: 'loading',
			error: null
		}
		this.stripe = null
		this.elements = null
		this.cardNumber = null
		this.cardExpiry = null
		this.cardCvc = null
		this.numberRef = React.createRef()
		this.expiryRef = React.createRef()
		this.cvcRef = React.createRef()
	}

	componentDidMount() {
		const locale = (this.props.lang) ? this.props.lang : 'auto'
		loadStripe(this.props.publishable_key)
		.then(stripe => {
			if (!stripe)
			{
				this.setState({ status: 'idle', error: this.props.assets.error_generic })
				return
			}
			this.stripe = stripe
			this.elements = stripe.elements({ locale: locale })
			const style = {
				base: {
					fontSize: '16px',
					color: '#1f2937',
					fontFamily: 'inherit',
					'::placeholder': { color: '#9ca3af' }
				},
				invalid: { color: '#ef4444' }
			}
			this.cardNumber = this.elements.create('cardNumber', { style: style, showIcon: true })
			this.cardExpiry = this.elements.create('cardExpiry', { style: style })
			this.cardCvc = this.elements.create('cardCvc', { style: style })
			this.cardNumber.mount(this.numberRef.current)
			this.cardExpiry.mount(this.expiryRef.current)
			this.cardCvc.mount(this.cvcRef.current)
			this.setState({ status: 'idle' })
		})
		.catch(() => {
			this.setState({ status: 'idle', error: this.props.assets.error_generic })
		})
	}

	componentWillUnmount() {
		if (this.cardNumber)
			this.cardNumber.destroy()
		if (this.cardExpiry)
			this.cardExpiry.destroy()
		if (this.cardCvc)
			this.cardCvc.destroy()
	}

	_mapError = (error) => {
		const assets = this.props.assets
		if (!error)
			return assets.error_generic
		if (error.decline_code === 'merchant_blacklist' || error.code === 'card_not_supported')
			return assets.error_foreign_card
		if (error.code === 'card_declined')
			return assets.error_declined
		if (error.message)
			return error.message
		return assets.error_generic
	}

	pay = () => {
		if (!this.stripe || this.state.status === 'processing' || this.state.status === 'succeeded')
			return
		this.setState({ status: 'processing', error: null })
		this.stripe.confirmCardPayment(this.props.client_secret, {
			payment_method: { card: this.cardNumber }
		})
		.then(result => {
			if (result.error)
			{
				this.setState({ status: 'idle', error: this._mapError(result.error) })
				return
			}
			// En capture manuelle, une autorisation réussie a le statut
			// "requires_capture" ; la capture (ou l'annulation si carte hors-UE) est
			// décidée côté serveur. "succeeded" couvre aussi le cas capture auto.
			const pi = result.paymentIntent
			if (pi && (pi.status === 'requires_capture' || pi.status === 'succeeded'))
			{
				this.setState({ status: 'succeeded' })
				this.props.onPaid(pi.id)
			}
			else
				this.setState({ status: 'idle', error: this.props.assets.error_generic })
		})
		.catch(() => {
			this.setState({ status: 'idle', error: this.props.assets.error_generic })
		})
	}

	render() {
		const assets = this.props.assets
		const themeColor = this.props.themeColor
		const loading = this.state.status === 'loading'
		const processing = this.state.status === 'processing'
		const fieldClass = "block w-full px-4 py-3 border-2 border-gray-200 rounded-2xl bg-white"
		return (
			<motion.div
				className="fixed inset-0 z-50 flex items-end justify-center bg-black bg-opacity-60"
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				onClick={this.props.onClose}
			>
				<motion.div
					className="bg-white w-full max-w-md rounded-t-3xl px-6 pt-6"
					style={{ paddingBottom: 'calc(var(--vh, 1vh) * 4 + 1.5rem)' }}
					initial={{ y: '100%' }}
					animate={{ y: 0 }}
					exit={{ y: '100%' }}
					transition={{ type: 'spring', stiffness: 300, damping: 30 }}
					onClick={(e) => e.stopPropagation()}
				>
					<div className="flex items-center justify-between mb-1">
						<h2 className="text-xl font-bold text-gray-800">{assets.title}</h2>
						<div className="text-lg font-bold" style={{ color: themeColor }}>{this.props.amountLabel}</div>
					</div>
					<p className="text-sm text-gray-400 mb-5">{assets.subtitle}</p>

					{loading ? (
						<div className="flex justify-center py-2">
							<Loader />
						</div>
					) : null}
					{/* Les conteneurs de champs sont rendus en permanence : Stripe y monte
					    les Elements via les refs dès que loadStripe a résolu. */}
					<div className="space-y-3">
						<div>
							<label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{assets.card_number}</label>
							<div className={fieldClass + " mt-1"} ref={this.numberRef} />
						</div>
						<div className="flex gap-3">
							<div className="flex-1">
								<label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{assets.expiry}</label>
								<div className={fieldClass + " mt-1"} ref={this.expiryRef} />
							</div>
							<div className="flex-1">
								<label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{assets.cvc}</label>
								<div className={fieldClass + " mt-1"} ref={this.cvcRef} />
							</div>
						</div>
					</div>

					{this.state.error ? (
						<div className="mt-4 rounded-2xl bg-red-50 border border-red-200 px-4 py-3">
							<p className="text-sm font-medium text-red-600">{this.state.error}</p>
						</div>
					) : null}

					<button
						type="button"
						onClick={this.pay}
						disabled={loading || processing}
						className="font-semibold py-4 w-full rounded-full text-white text-base mt-6 disabled:opacity-60"
						style={{ backgroundColor: themeColor }}
					>
						{processing ? assets.processing : assets.pay_button}
					</button>
					<button
						type="button"
						onClick={this.props.onClose}
						disabled={processing}
						className="font-medium py-3 w-full text-gray-400 text-sm mt-1 disabled:opacity-40"
					>
						{assets.cancel}
					</button>
				</motion.div>
			</motion.div>
		)
	}
}

export default Checkout
